import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  InputAdornment,
  Typography,
} from '@mui/material';
import { RiskClassification } from 'app/config/constants';
import { TruckGroupSelectionField } from 'app/shared/components/truck-group-selection/TruckGroupSelectionField';
import { RiskClassificationSelector } from 'app/shared/components/risk-classification/RiskClassificationSelector';
import { CustomSwitch, FormControlDefault, TextFieldForms, TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import React from 'react';
import { translate } from 'react-jhipster';

interface IVirtualFenceFormsProps {
  saveEntity: (values: any) => void;
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  radius: number;
  setRadius: (v: number) => void;
  riskClassification: RiskClassification;
  setRiskClassification: (v: RiskClassification) => void;
  notificationEnabled: boolean;
  setNotificationEnabled: (v: boolean) => void;
  selectedGroupIds: number[];
  setSelectedGroupIds: (ids: number[]) => void;
  minRadius: number;
  maxRadius: number;
  readOnly?: boolean;
}

export const VirtualFenceForms = ({
  saveEntity,
  name,
  setName,
  description,
  setDescription,
  radius,
  setRadius,
  riskClassification,
  setRiskClassification,
  notificationEnabled,
  setNotificationEnabled,
  selectedGroupIds,
  setSelectedGroupIds,
  minRadius,
  maxRadius,
  readOnly = false,
}: IVirtualFenceFormsProps) => {
  const radiusHasError = radius < minRadius || radius > maxRadius;

  const getRadiusHelperText = () => {
    if (radius < minRadius) return `Raio mínimo de ${minRadius} metros`;
    if (radius > maxRadius) return `Raio máximo de ${maxRadius} metros`;
    return undefined;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (readOnly) return;
    if (!name?.trim() || radiusHasError) return;
    saveEntity({ name: name.trim(), description, radius, riskClassification, notificationEnabled });
  };

  const formControlSx = { my: 0, width: '100%' };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', height: '100%' }}>

      <Box display="flex" flexDirection="column" gap={1.5}>
        <FormControlDefault variant="outlined" sx={formControlSx}>
          <TypographyForms variant="body2">{`${translate('fleetSenseApp.virtualFenceCircle.name')} *`}</TypographyForms>
          <TextFieldForms
            id="virtual-fence-circle-name"
            name="name"
            data-cy="name"
            size="small"
            value={name}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
            placeholder={translate('fleetSenseApp.virtualFenceCircle.name')}
            disabled={readOnly}
            required
            fullWidth
          />
        </FormControlDefault>

        <FormControlDefault variant="outlined" sx={formControlSx}>
          <TypographyForms variant="body2">{translate('fleetSenseApp.virtualFenceCircle.description')}</TypographyForms>
          <TextFieldForms
            id="virtual-fence-circle-description"
            name="description"
            data-cy="description"
            size="small"
            value={description}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDescription(event.target.value)}
            placeholder={translate('fleetSenseApp.virtualFenceCircle.description')}
            disabled={readOnly}
            multiline
            minRows={1}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': { alignItems: 'flex-start', py: '10px' },
              '& textarea.MuiInputBase-inputMultiline': { p: '0 !important', lineHeight: 1.4 },
            }}
          />
        </FormControlDefault>

        <FormControlDefault variant="outlined" sx={formControlSx}>
          <TypographyForms variant="body2">{`${translate('fleetSenseApp.virtualFenceCircle.radius')} *`}</TypographyForms>
          <TextFieldForms
            id="virtual-fence-circle-radius"
            name="radius"
            data-cy="radius"
            size="small"
            value={Number.isFinite(radius) ? radius : ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const value = Number(event.target.value);
              setRadius(Number.isNaN(value) ? 0 : value);
            }}
            onBlur={() => {
              if (radius < minRadius) setRadius(minRadius);
              else if (radius > maxRadius) setRadius(maxRadius);
            }}
            type="number"
            disabled={readOnly}
            error={radiusHasError}
            helperText={getRadiusHelperText()}
            InputProps={{
              endAdornment: <InputAdornment position="end">m</InputAdornment>,
            }}
            inputProps={{ min: minRadius, max: maxRadius, step: 1 }}
            required
            fullWidth
          />
        </FormControlDefault>
      </Box>

      <Divider />

      <Box display="flex" flexDirection="column" gap={1.5}>
        <FormControlDefault variant="outlined" sx={formControlSx}>
          <TypographyForms variant="body2" sx={{ mb: 0.5 }}>Classificação *</TypographyForms>
          <RiskClassificationSelector value={riskClassification} onChange={setRiskClassification} disabled={readOnly} />
        </FormControlDefault>

        <FormControlLabel
          control={
            <CustomSwitch
              checked={notificationEnabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotificationEnabled(e.target.checked)}
              disabled={readOnly}
              size="small"
              data-cy="notificationEnabled"
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Receber notificação ao entrar/sair da cerca
            </Typography>
          }
          sx={{ ml: '-2px', mt: 0.5 }}
        />
      </Box>

      <Divider />

      <TruckGroupSelectionField selectedGroupIds={selectedGroupIds} onChange={setSelectedGroupIds} disabled={readOnly} />

      <Button
        id="save-entity"
        data-cy="entityCreateSaveButton"
        type="submit"
        variant="contained"
        color="secondary"
        startIcon={<SaveRoundedIcon />}
        disabled={readOnly || !name?.trim() || radiusHasError}
        fullWidth
        sx={{ mt: 'auto', borderRadius: '8px', textTransform: 'none' }}
      >
        {readOnly ? 'Somente visualização' : 'Salvar'}
      </Button>
    </Box>
  );
};
